/**
 * StaffUsage — IT-SCE Intern: Cockpit-Verbrauchsanzeige, alle Tenants.
 *
 * TQ #10589 (SPEC-Cockpit-Verbrauchsanzeige.md §4b/§5, Zielbild PROJ-Cortex-Billing).
 * Zeigt jede Tenant-Zeile mit Pool-Einheiten + EUR (Kunde). Klick auf eine
 * Zeile lädt den Staff-Detail-Endpoint (interne Kosten + Marge + Consistency-
 * Warnungen, SPEC §7 Q3: NIE kundenfakturiert, nur hier sichtbar).
 *
 * Backend: GET /api/staff/usage (Übersicht) + GET /api/staff/usage/:tenant_slug
 * (Detail), siehe services/portal-api/internal/handlers/org_usage.go.
 *
 * Routing: outer-router-level (App.tsx) vor /:slug/*, hinter StaffOnly-Guard.
 */

import { Fragment, useEffect, useState } from 'react'
import { api } from '../../services/api'
import type { UsageResponse } from '../../services/api'

function formatUnits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} Mio`
  if (n >= 1_000) return `${(n / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })}k`
  return n.toLocaleString('de-DE')
}

function formatEUR(n: number): string {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

function totalUnits(row: UsageResponse): number {
  const c = row.categories ?? {}
  return (c.aria_chat?.units ?? 0) + (c.task_work?.units ?? 0) + (c.human_events?.units ?? 0)
}

function totalEUR(row: UsageResponse): number {
  const c = row.categories ?? {}
  return (c.aria_chat?.eur ?? 0) + (c.task_work?.eur ?? 0) + (c.human_events?.eur ?? 0)
}

export default function StaffUsage() {
  const [rows, setRows] = useState<UsageResponse[]>([])
  const [month, setMonth] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [expanded, setExpanded] = useState<string | null>(null)
  const [detail, setDetail] = useState<UsageResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = () => {
    setLoading(true)
    setError(null)
    api
      .staffGetUsage()
      .then((resp) => {
        setRows(resp.tenants)
        setMonth(resp.month)
      })
      .catch((err) => {
        console.error('[StaffUsage] load failed:', err)
        setError('Verbrauchsdaten konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const toggleRow = (tenant: string) => {
    if (expanded === tenant) {
      setExpanded(null)
      setDetail(null)
      return
    }
    setExpanded(tenant)
    setDetail(null)
    setDetailLoading(true)
    api
      .staffGetUsageDetail(tenant)
      .then(setDetail)
      .catch((err) => {
        console.error('[StaffUsage] detail load failed:', err)
        setError(`Detail für ${tenant} konnte nicht geladen werden.`)
      })
      .finally(() => setDetailLoading(false))
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <a href="/" style={{ color: '#FF6B35', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← Cockpit
        </a>
      </div>
      <h1 style={{ margin: 0, fontSize: '1.6rem' }}>📊 Verbrauch — Tenant-Übersicht</h1>
      {month && <p style={{ color: '#666', fontSize: '0.9em', marginTop: '0.25rem' }}>{month}</p>}

      {error && <p style={{ color: '#c00' }}>{error}</p>}

      {loading ? (
        <p>Lade …</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#666' }}>Keine Verbrauchsdaten für diesen Monat.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem' }}>Tenant</th>
              <th style={{ padding: '0.5rem' }}>Pool-Einheiten</th>
              <th style={{ padding: '0.5rem' }}>EUR (Kunde)</th>
              <th style={{ padding: '0.5rem' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Fragment key={row.tenant}>
                <tr
                  onClick={() => toggleRow(row.tenant)}
                  style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                >
                  <td style={{ padding: '0.5rem', fontWeight: 600 }}>{row.tenant}</td>
                  <td style={{ padding: '0.5rem' }}>{formatUnits(totalUnits(row))}</td>
                  <td style={{ padding: '0.5rem' }}>{formatEUR(totalEUR(row))}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', color: '#888' }}>
                    {expanded === row.tenant ? '▾' : '▸'}
                  </td>
                </tr>
                {expanded === row.tenant && (
                  <tr key={`${row.tenant}-detail`}>
                    <td colSpan={4} style={{ padding: '0.75rem 1.5rem', background: '#fafafa' }}>
                      {detailLoading ? (
                        <span>Lade Detail …</span>
                      ) : detail ? (
                        <div style={{ fontSize: '0.9em' }}>
                          <div>
                            <strong>Interne Ist-Kosten:</strong>{' '}
                            {detail.internal_cost_eur != null ? formatEUR(detail.internal_cost_eur) : '–'}
                          </div>
                          <div>
                            <strong>Marge:</strong>{' '}
                            {detail.margin_pct != null ? `${detail.margin_pct.toFixed(1)} %` : '–'}
                          </div>
                          {detail.consistency_warnings && detail.consistency_warnings.length > 0 && (
                            <div style={{ color: '#c00', marginTop: 4 }}>
                              ⚠ {detail.consistency_warnings.join(', ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#888' }}>Kein Detail verfügbar.</span>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
